import React, { useRef, useMemo } from 'react';
import { motion } from 'framer-motion';
import { Letter, Position } from '../types/game';
import { getLetterPointValue } from '../utils/wordValidator';
import TileExplosion from './TileExplosion';

// Module loaded - removed verbose logging

interface LetterTileProps {
  letter: Letter;
  isSelected: boolean;
  isInPath: boolean;
  isAvailable: boolean;
  onClick: (position: Position) => void;
  isRemoving?: boolean;
  isShifting?: boolean;
  isHinted?: boolean;
  isHintedNext?: boolean;
  fallDistance?: number; // Distance in pixels the tile needs to fall
  initialYOffset?: number; // Initial Y offset to position tile at old location
  isValidatingWord?: boolean; // True when word is being validated (show red tint)
  wordValidationResult?: 'valid' | 'invalid' | null; // Result of validation
}

const LetterTile: React.FC<LetterTileProps> = ({
  letter,
  isSelected,
  isInPath,
  isAvailable,
  onClick,
  isRemoving = false,
  isShifting = false,
  isHinted = false,
  isHintedNext = false,
  fallDistance = 65, // Default: one tile height (60px) + gap (5px)
  initialYOffset = 0, // Start at current position by default
  isValidatingWord = false,
  wordValidationResult = null
}) => {
  
  // Track explosion animation state - use a ref that persists across renders
  // This helps us create a stable key that only changes when animation starts
  const explosionKeyRef = useRef<string>('');
  const wasRemovingRef = useRef(false);
  
  // Update explosion key synchronously when isRemoving changes to trigger remount
  // Do this in render phase, not useEffect, to ensure remount happens immediately
  if (isRemoving && !wasRemovingRef.current) {
    // Explosion just started - generate unique key to force remount
    explosionKeyRef.current = `exploding-${letter.position.row}-${letter.position.col}-${Date.now()}`;
    wasRemovingRef.current = true;
  } else if (!isRemoving && wasRemovingRef.current) {
    // Explosion finished - reset key
    explosionKeyRef.current = '';
    wasRemovingRef.current = false;
  }

  const handleClick = () => {
    if (!letter.used) {
      onClick(letter.position);
    }
  };

  const getTileStyle = () => {
    // CRITICAL: Hint styles take priority - apply hint styling first
    // This ensures hints are visible even if tile is selected or available
    if (isHinted || isHintedNext) {
      const baseStyle = {
        backgroundColor: 'rgba(33, 150, 243, 0.12)',
        color: '#333',
        cursor: 'pointer',
        border: '3px solid #2196F3',
        boxShadow: '0 0 14px rgba(33, 150, 243, 0.8)'
      };
      
      // If also selected, keep hint styling but make it more visible
      if (isSelected) {
        return {
          ...baseStyle,
          backgroundColor: 'rgba(33, 150, 243, 0.25)',
          color: '#1565C0'
        };
      }
      
      return baseStyle;
    }
    
    if (letter.used) {
      return {
        backgroundColor: '#e0e0e0',
        color: '#999',
        cursor: 'not-allowed'
      };
    }
    
    // Show red tint during validation or invalid result
    if (isValidatingWord || wordValidationResult === 'invalid') {
      return {
        backgroundColor: wordValidationResult === 'invalid' ? '#f44336' : '#ff6b6b',
        color: 'white',
        cursor: 'pointer',
        boxShadow: wordValidationResult === 'invalid' ? '0 0 15px rgba(244, 67, 54, 0.5)' : '0 0 15px rgba(255, 107, 107, 0.5)'
      };
    }
    
    if (isSelected) {
      return {
        backgroundColor: '#4CAF50',
        color: 'white',
        cursor: 'pointer',
        boxShadow: '0 0 15px rgba(76, 175, 80, 0.5)'
      };
    }
    
    if (isInPath) {
      return {
        backgroundColor: '#81C784',
        color: 'white',
        cursor: 'pointer'
      };
    }
    
    if (isAvailable) {
      return {
        backgroundColor: '#E8F5E8',
        color: '#2E7D32',
        cursor: 'pointer',
        border: '2px solid #4CAF50',
        boxShadow: '0 0 10px rgba(76, 175, 80, 0.3)'
      };
    }
    
    
    return {
      backgroundColor: '#f5f5f5',
      color: '#333',
      cursor: 'pointer'
    };
  };

  // Animation variants - removed 'removing' variant to prevent interference with inline animation
  const tileVariants = useMemo(() => ({
    initial: { 
      scale: 1, 
      opacity: 1, 
      rotate: 0,
      transition: { duration: 0 }
    },
    shifting: {
      // Will be dynamically set based on fallDistance prop
      scale: [1, 1.02, 1, 1], // Subtle scale on bounce
      transition: { 
        duration: 0.8, // Longer, smoother fall
        ease: [0.25, 0.46, 0.45, 0.94], // Smooth acceleration with slight bounce
        times: [0, 0.1, 0.15, 1]
      }
    },
    normal: {
      scale: 1,
      opacity: 1,
      y: 0,
      rotate: 0,
      transition: { duration: 0.2 }
    }
  }), []);

  // Track when component mounts with falling props for debugging
  // Only log once per animation, not on every render
  const mountedRef = React.useRef(false);
  
  // Generate key that forces remount when explosion starts, then stays stable
  // This ensures the initial prop is applied correctly when isRemoving becomes true
  // For falling tiles, use a stable key that includes the animation state to prevent remounts
  // CRITICAL: When isRemoving is true, use the explosionKeyRef to force remount immediately
  // This ensures the component remounts as soon as isRemoving becomes true, starting animation
  const stableKey = (isRemoving && explosionKeyRef.current) 
    ? explosionKeyRef.current
    : (isShifting ? `falling-${letter.position.row}-${letter.position.col}-${initialYOffset}` : 
       `${letter.position.row}-${letter.position.col}-${letter.char}`);
  
  // Removed verbose logging - was causing delays

  // Render empty spaces as empty tiles
  if (letter.char === ' ' || letter.char === '') {
    return (
      <motion.div
        className="letter-tile empty-tile"
        style={{
          backgroundColor: 'transparent',
          border: '2px dashed #ccc',
          color: 'transparent',
          cursor: 'default'
        }}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.3, delay: 0.2 }}
      >
        &nbsp;
      </motion.div>
    );
  }

  // Removed verbose render logs - they were causing delay

  return (
    <motion.div
      key={stableKey}
      className={`letter-tile${isHinted ? ' hinted' : ''}${isHintedNext ? ' hinted-next' : ''}`}
      style={{
        ...getTileStyle(),
        // Hint styles are now in getTileStyle() - no need to apply again here
        position: 'relative', // Always relative so badges position correctly
        // CRITICAL: Optimize for smooth animations
        willChange: (isShifting || isRemoving) ? 'transform' : 'auto',
        backfaceVisibility: 'hidden',
        WebkitBackfaceVisibility: 'hidden',
        // Disable containment during animations
        contain: (!isShifting && !isRemoving) ? 'layout style paint' : 'none',
        // Force GPU acceleration - use transform3d for better performance
        transform: (isShifting || isRemoving) ? 'translate3d(0, 0, 0)' : undefined,
        // Prevent text selection during animations
        userSelect: (isShifting || isRemoving) ? 'none' : undefined,
      }}
      onClick={handleClick}
      title={isSelected ? "Click to unselect" : letter.used ? "Already used" : "Click to select"}
      variants={undefined} // Disable variants completely to prevent interference
      initial={
        // Always set initial to ensure animation starts from correct state
        // CRITICAL: When isRemoving is true, set initial to a distinct state
        // Include all transform properties to ensure Framer Motion detects the change
        isRemoving ? { scale: 0.8, opacity: 1, rotate: 0, x: 0, y: 0 } :
        (isShifting && initialYOffset !== 0) ? { 
          y: initialYOffset, 
          x: 0, 
          scale: 1, 
          opacity: 1,
          rotate: 0
        } :
        { scale: 1, opacity: 1, rotate: 0, y: 0, x: 0 }
      }
      // Track when component mounts with falling props
      // Removed onAnimationStart logging - was causing delays
      animate={
        isRemoving ? {
          scale: [0.8, 1.2, 1.8, 0],
          opacity: [1, 1, 0.8, 0],
          rotate: [0, 180, 360],
          transition: { 
            duration: 2.5,
            ease: [0.34, 1.56, 0.64, 1], // Optimized easing - fewer keyframes = smoother
            type: "tween",
            times: [0, 0.3, 0.7, 1],
            layout: false, // CRITICAL: Prevent layout animations during explosion
            restSpeed: 0.01,
            restDelta: 0.01,
            onComplete: () => {
              explosionKeyRef.current = '';
            }
          }
        } :
        (isShifting && initialYOffset !== 0) ? {
          y: 0, // Animate from initialYOffset (old position) to 0 (final position)
          transition: { 
            duration: 0.8, // Slightly longer for smoother feel
            ease: [0.25, 0.1, 0.25, 1], // Smooth cubic bezier easing (ease-out)
            type: "tween",
            layout: false, // CRITICAL: Disable layout animations
            restSpeed: 0.01,
            restDelta: 0.01,
          }
        } :
        isValidatingWord ? {
          scale: [1, 1.05, 1], // Pulse effect
          transition: {
            duration: 0.8,
            ease: "easeInOut",
            repeat: Infinity,
            repeatType: "reverse" as const,
          }
        } :
        wordValidationResult === 'invalid' ? {
          scale: 1,
          transition: {
            duration: 0.5, // Smooth fade back to normal
            ease: "easeOut",
          }
        } :
        "normal"
      }
      whileHover={!letter.used ? { scale: 1.05 } : {}}
      whileTap={!letter.used ? { scale: 0.95 } : {}}
    >
      {isRemoving && (
        <TileExplosion
          position={{ top: '50%', left: '50%' }}
          color={isSelected || isInPath ? '#4CAF50' : '#667eea'}
        />
      )}
      {letter.char}
      {(() => {
        const pointValue = getLetterPointValue(letter.char);
        const hasBonus = pointValue > 0;
        return hasBonus && (
          <div
            style={{
              position: 'absolute',
              top: '2px',
              right: '2px',
              fontSize: '8px',
              fontWeight: 'bold',
              backgroundColor: letter.used 
                ? 'rgba(0,0,0,0.3)' 
                : pointValue === 20 
                  ? 'rgba(255,215,0,0.95)' 
                  : pointValue === 12 
                    ? 'rgba(255,200,87,0.95)' 
                    : 'rgba(255,235,156,0.95)',
              color: letter.used ? '#999' : pointValue === 20 ? '#B8860B' : pointValue === 12 ? '#B8860B' : '#8B6914',
              padding: '1px 4px',
              borderRadius: '4px',
              lineHeight: '1.2',
              pointerEvents: 'none',
              boxShadow: letter.used ? 'none' : '0 1px 3px rgba(0,0,0,0.25)',
              border: letter.used ? 'none' : '1px solid rgba(184, 134, 11, 0.3)',
              minWidth: '14px',
              textAlign: 'center'
            }}
            title={`Letter bonus: +${pointValue} points`}
          >
            +{pointValue}
          </div>
        );
      })()}
      {isSelected && (
        <div
          style={{
            position: 'absolute',
            bottom: '2px',
            left: '2px',
            fontSize: '10px',
            fontWeight: 'bold',
            opacity: 0.7,
            pointerEvents: 'none'
          }}
        >
          ×
        </div>
      )}
    </motion.div>
  );
};

export default React.memo(LetterTile, (prev, next) => {
  // Avoid re-render unless something visible changed
  const sameLetter = prev.letter.char === next.letter.char && prev.letter.used === next.letter.used && prev.letter.position.row === next.letter.position.row && prev.letter.position.col === next.letter.position.col;
  const sameRemoving = (prev.isRemoving ?? false) === (next.isRemoving ?? false);
  const sameShifting = (prev.isShifting ?? false) === (next.isShifting ?? false);
  const sameInitialYOffset = prev.initialYOffset === next.initialYOffset;
  const sameFallDistance = prev.fallDistance === next.fallDistance;
  const sameSelected = prev.isSelected === next.isSelected;
  const sameInPath = prev.isInPath === next.isInPath;
  const sameAvailable = prev.isAvailable === next.isAvailable;
  
  // CRITICAL: Check both isHinted and isHintedNext - hint changes must trigger re-render
  const sameHinted = (prev.isHinted ?? false) === (next.isHinted ?? false);
  const sameHintedNext = (prev.isHintedNext ?? false) === (next.isHintedNext ?? false);
  
  // If hint changed, always re-render
  if (!sameHinted || !sameHintedNext) {
    return false; // Force re-render
  }
  
  // For falling animations, props must be EXACTLY the same to prevent remounts
  if (sameShifting) {
    // If shifting, initialYOffset and fallDistance must match exactly
    if (sameInitialYOffset && sameFallDistance) {
      // During shifting, still check selection props - they might change!
      if (sameSelected && sameInPath && sameAvailable) {
        // All props match - prevent re-render
        return true;
      } else {
        // Selection props changed - allow re-render even during shifting
        return false;
      }
    } else {
      // Props changed - allow re-render
      return false;
    }
  }
  
  // Not shifting - check all props including selection
  const shouldSkipRender = (
    sameLetter &&
    sameSelected &&
    sameInPath &&
    sameAvailable &&
    sameRemoving &&
    sameShifting &&
    sameInitialYOffset &&
    sameFallDistance
  );
  
  return shouldSkipRender;
});
